import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { HelpCircle, X, Search, Send, Sparkles, ChevronRight, Clock3, Rocket, ArrowRight, Play, Loader2, MessageCircleQuestion } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HelpContext = createContext(null);
export const useHelp = () => useContext(HelpContext) || { openHelp: () => {}, startTour: () => {}, available: false };

const RESTAURANT_ROLES = ["owner", "manager", "staff"];

export function HelpProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const available = user && RESTAURANT_ROLES.includes(user.role);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [tour, setTour] = useState(null); // {steps, route, i, id}

  const load = useCallback(() => {
    if (!available) return;
    api.get("/help").then((r) => setData(r.data)).catch(() => {});
  }, [available]);
  useEffect(() => { load(); }, [load]);

  const openHelp = useCallback((articleId = null) => { if (!available) return; setActiveId(articleId); setOpen(true); }, [available]);
  const closeHelp = () => setOpen(false);

  const startTour = useCallback((tourId) => {
    const t = data?.tours?.find((x) => x.id === tourId);
    if (!t) return;
    setOpen(false);
    navigate(t.route);
    setTimeout(() => setTour({ ...t, i: 0 }), 500);
  }, [data, navigate]);

  const endTour = (completed) => {
    if (completed && tour) api.post(`/help/tours/${tour.id}/complete`).catch(() => {});
    setTour(null);
    if (completed) load();
  };

  const markViewed = (id) => { api.post(`/help/articles/${id}/view`).then((r) => setData((d) => d ? { ...d, recent: r.data.recent } : d)).catch(() => {}); };

  return (
    <HelpContext.Provider value={{ openHelp, startTour, available }}>
      {children}
      {available && open && <HelpModal data={data} activeId={activeId} setActiveId={setActiveId} onClose={closeHelp} onStartTour={startTour} onView={markViewed} reload={load} />}
      {available && tour && <GuidedTour tour={tour} setTour={setTour} onEnd={endTour} />}
    </HelpContext.Provider>
  );
}

export function HelpTip({ articleId, className = "" }) {
  const { openHelp, available } = useHelp();
  if (!available) return null;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); openHelp(articleId); }} data-testid={`help-tip-${articleId}`}
      title="Help" className={`inline-flex items-center justify-center w-5 h-5 rounded-full border border-white/20 text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors ${className}`}>
      <HelpCircle className="w-3.5 h-3.5" />
    </button>
  );
}

const IconFor = ({ name, ...p }) => { const C = Icons[name] || Icons.BookOpen; return <C {...p} />; };

function ArticleView({ article, onStartTour, tours }) {
  const relTour = tours?.find((t) => (t.route === "/attendance" && article.id.includes("attendance")) || (t.route === "/rota" && article.id === "mgr-rota") || (t.route === "/academy" && article.id === "mgr-training") || (t.route === "/dashboard" && article.id === "mgr-dashboard"));
  return (
    <div className="space-y-5" data-testid={`help-article-${article.id}`}>
      <div className="flex items-center gap-2">
        <IconFor name={article.icon} className="w-5 h-5 text-[#D4AF37]" />
        <h2 className="font-serif-lux text-2xl">{article.title}</h2>
        {!article.available && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#D97706]/15 text-[#D97706] border border-[#D97706]/30" data-testid="coming-soon-badge">Coming soon</span>}
      </div>
      <div><p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">What this does</p><p className="text-sm text-white/70">{article.what}</p></div>
      <div><p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Why you'd use it</p><p className="text-sm text-white/70">{article.why}</p></div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Step by step</p>
        <ol className="space-y-2">{article.steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm text-white/80"><span className="shrink-0 w-6 h-6 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center text-xs">{i + 1}</span><span>{s}</span></li>
        ))}</ol>
      </div>
      {relTour && article.available && (
        <button onClick={() => onStartTour(relTour.id)} data-testid="start-tour-from-article" className="inline-flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] rounded-lg px-4 py-2 text-sm hover:bg-[#D4AF37]/10"><Play className="w-4 h-4" />Show me — start walkthrough</button>
      )}
      {article.tips.length > 0 && <div className="rounded-lg bg-[#8A9A5B]/10 border border-[#8A9A5B]/25 p-3"><p className="text-[10px] uppercase tracking-widest text-[#8A9A5B] mb-1">Helpful tips</p><ul className="list-disc list-inside text-sm text-white/70 space-y-1">{article.tips.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
      {article.mistakes.length > 0 && <div className="rounded-lg bg-[#DC2626]/10 border border-[#DC2626]/25 p-3"><p className="text-[10px] uppercase tracking-widest text-[#DC2626] mb-1">Common mistakes</p><ul className="list-disc list-inside text-sm text-white/70 space-y-1">{article.mistakes.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
      {article.faqs.length > 0 && <div><p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">FAQs</p><div className="space-y-2">{article.faqs.map((f, i) => <div key={i} className="border border-white/5 rounded-lg p-3"><p className="text-sm text-white/90">{f.q}</p><p className="text-sm text-white/50 mt-1">{f.a}</p></div>)}</div></div>}
    </div>
  );
}

function Assistant() {
  const [msgs, setMsgs] = useState([{ role: "assistant", content: "Hi! Ask me how to do anything on the platform — e.g. 'How do I clock in?' or 'How do I publish the rota?'" }]);
  const [q, setQ] = useState(""); const [busy, setBusy] = useState(false); const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const ask = async () => {
    if (!q.trim() || busy) return;
    const question = q.trim(); setQ("");
    const history = msgs.slice(1).map((m) => ({ role: m.role, content: m.content }));
    setMsgs((m) => [...m, { role: "user", content: question }]); setBusy(true);
    try { const { data } = await api.post("/help/assistant", { question, history }); setMsgs((m) => [...m, { role: "assistant", content: data.answer }]); }
    catch (e) { setMsgs((m) => [...m, { role: "assistant", content: formatError(e?.response?.data?.detail) || "Assistant unavailable." }]); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col h-full" data-testid="help-assistant">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {msgs.map((m, i) => <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}><div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-line ${m.role === "user" ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-white/5 text-white/80"}`}>{m.content}</div></div>)}
        {busy && <div className="flex justify-start"><div className="bg-white/5 px-3 py-2 rounded-lg"><Loader2 className="w-4 h-4 animate-spin text-white/40" /></div></div>}
        <div ref={end} />
      </div>
      <div className="flex gap-2 mt-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="How do I…?" data-testid="help-assistant-input" className="lux-input flex-1 rounded-lg px-3 py-2.5 text-sm" />
        <button onClick={ask} disabled={busy} data-testid="help-assistant-send" className="gold-btn rounded-lg px-4"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function HelpModal({ data, activeId, setActiveId, onClose, onStartTour, onView }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("browse");
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const active = activeId && data?.articles?.find((a) => a.id === activeId);
  useEffect(() => { if (active) onView(active.id); }, [activeId]); // eslint-disable-line

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(() => { api.get(`/help/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data.results)).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [q]);

  if (!data) return null;
  const cats = {};
  data.articles.forEach((a) => { (cats[a.category] = cats[a.category] || []).push(a); });
  const recentArts = (data.recent || []).map((id) => data.articles.find((a) => a.id === id)).filter(Boolean);
  const quickArts = (data.quick_start || []).map((id) => data.articles.find((a) => a.id === id)).filter(Boolean);
  const go = (id) => { setActiveId(id); setTab("browse"); };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex justify-end" onClick={onClose} data-testid="help-center">
      <div className="w-full max-w-2xl h-full bg-[#111113] border-l border-white/10 flex flex-col animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2"><HelpCircle className="w-5 h-5 text-[#D4AF37]" /><h1 className="font-serif-lux text-2xl">Help Centre</h1><span className="text-[10px] text-white/30">v{data.version}</span></div>
          <button onClick={onClose} data-testid="help-close"><X className="w-5 h-5 text-white/50 hover:text-white" /></button>
        </div>
        <div className="px-5 pt-4 flex gap-2 border-b border-white/5">
          <button onClick={() => { setTab("browse"); setActiveId(null); }} data-testid="help-tab-browse" className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === "browse" ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/50"}`}>Guides</button>
          <button onClick={() => setTab("assistant")} data-testid="help-tab-assistant" className={`px-3 py-2 text-sm border-b-2 -mb-px flex items-center gap-1 ${tab === "assistant" ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/50"}`}><Sparkles className="w-3.5 h-3.5" />Ask Assistant</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === "assistant" ? <Assistant /> : active ? (
            <div>
              <button onClick={() => setActiveId(null)} className="text-xs text-white/50 hover:text-[#D4AF37] mb-4">← Back to guides</button>
              <ArticleView article={active} onStartTour={onStartTour} tours={data.tours} />
            </div>
          ) : (
            <div className="space-y-6">
              <div className="relative">
                <Search className="w-4 h-4 text-white/30 absolute start-3 inset-y-0 my-auto pointer-events-none" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guides — clock in, payroll, leave…" data-testid="help-search" className="lux-input w-full rounded-lg ps-10 pe-3 py-2.5 text-sm" />
              </div>
              {results ? (
                <div data-testid="help-search-results">
                  <p className="text-xs text-white/40 mb-2">{results.length} results</p>
                  {results.map((r) => <button key={r.id} onClick={() => go(r.id)} className="block w-full text-left p-3 rounded-lg border border-white/5 hover:border-white/20 mb-2"><p className="text-sm text-white/90">{r.title}</p><p className="text-xs text-white/40">{r.category} — {r.snippet}</p></button>)}
                  {results.length === 0 && <p className="text-sm text-white/40">No guides match. Try a different word, or ask the Help Assistant.</p>}
                </div>
              ) : (
                <>
                  {data.tours?.length > 0 && (
                    <div><p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">Interactive walkthroughs</p>
                      <div className="flex flex-wrap gap-2">{data.tours.map((t) => <button key={t.id} onClick={() => onStartTour(t.id)} data-testid={`tour-${t.id}`} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"><Play className="w-3 h-3" />{t.title}</button>)}</div>
                    </div>
                  )}
                  <div><p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2 flex items-center gap-1"><Rocket className="w-3.5 h-3.5" />Quick start</p>
                    <div className="grid sm:grid-cols-2 gap-2">{quickArts.map((a) => <button key={a.id} onClick={() => go(a.id)} data-testid={`quickstart-${a.id}`} className="flex items-center gap-2 text-left p-3 rounded-lg border border-white/5 hover:border-white/20"><IconFor name={a.icon} className="w-4 h-4 text-[#D4AF37] shrink-0" /><span className="text-sm text-white/80">{a.title}</span></button>)}</div>
                  </div>
                  {recentArts.length > 0 && <div><p className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" />Recently viewed</p><div className="flex flex-wrap gap-2">{recentArts.map((a) => <button key={a.id} onClick={() => go(a.id)} className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/70 hover:text-[#D4AF37]">{a.title}</button>)}</div></div>}
                  {Object.entries(cats).map(([cat, arts]) => (
                    <div key={cat} data-testid={`help-category-${cat}`}>
                      <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{cat}</p>
                      <div className="space-y-1">{arts.map((a) => (
                        <button key={a.id} onClick={() => go(a.id)} data-testid={`help-article-link-${a.id}`} className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-white/5 text-left">
                          <span className="flex items-center gap-2 text-sm text-white/80"><IconFor name={a.icon} className="w-4 h-4 text-white/40" />{a.title}{!a.available && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded-full bg-[#D97706]/15 text-[#D97706]">soon</span>}</span>
                          <ChevronRight className="w-4 h-4 text-white/30" />
                        </button>
                      ))}</div>
                    </div>
                  ))}
                  <div className="rounded-lg border border-white/10 p-4 flex items-center justify-between" data-testid="help-contact">
                    <div className="flex items-center gap-2"><MessageCircleQuestion className="w-5 h-5 text-[#D4AF37]" /><div><p className="text-sm text-white/80">{data.role === "staff" ? "Still stuck? Ask your manager" : "Need more help?"}</p><p className="text-xs text-white/40">{data.role === "staff" ? "Send them a message in seconds." : "Message our support team — we'll get back to you."}</p></div></div>
                    {data.role === "staff" ? (
                      <button onClick={() => { onClose(); navigate("/messages"); }} data-testid="help-contact-action" className="text-xs text-[#D4AF37] hover:underline flex items-center gap-1">Open Messages <ArrowRight className="w-3 h-3" /></button>
                    ) : (
                      <button onClick={() => { onClose(); navigate("/support"); }} data-testid="help-contact-action" className="text-xs text-[#D4AF37] hover:underline flex items-center gap-1">Contact support <ArrowRight className="w-3 h-3" /></button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GuidedTour({ tour, setTour, onEnd }) {
  const step = tour.steps[tour.i];
  const [rect, setRect] = useState(null);
  useEffect(() => {
    let tries = 0;
    const find = () => {
      const el = document.querySelector(`[data-testid="${step.selector}"]`);
      if (el) { el.scrollIntoView({ block: "center", behavior: "smooth" }); setRect(el.getBoundingClientRect()); }
      else if (tries++ < 10) setTimeout(find, 300);
      else setRect(null);
    };
    setRect(null); find();
  }, [tour.i, step.selector]);
  const last = tour.i === tour.steps.length - 1;
  const pos = rect ? { top: Math.min(rect.bottom + 12, window.innerHeight - 200), left: Math.min(Math.max(rect.left, 16), window.innerWidth - 340) } : { top: "40%", left: "50%", transform: "translate(-50%,-50%)" };
  return (
    <div className="fixed inset-0 z-[70]" data-testid="guided-tour">
      <div className="absolute inset-0 bg-black/60" />
      {rect && <div className="absolute border-2 border-[#D4AF37] rounded-lg pointer-events-none transition-all" style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8, boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)" }} />}
      <div className="absolute w-[320px] bg-[#1A1A1D] border border-[#D4AF37]/40 rounded-lg p-4 shadow-2xl" style={pos} data-testid="tour-tooltip">
        <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">{tour.title} · {tour.i + 1}/{tour.steps.length}</p>
        <p className="font-serif-lux text-lg mb-1">{step.title}</p>
        <p className="text-sm text-white/70 mb-4">{step.body}</p>
        <div className="flex items-center justify-between">
          <button onClick={() => onEnd(false)} data-testid="tour-skip" className="text-xs text-white/40 hover:text-white">Skip</button>
          <div className="flex gap-2">
            {tour.i > 0 && <button onClick={() => setTour({ ...tour, i: tour.i - 1 })} className="text-xs px-3 py-1.5 rounded-lg border border-white/15">Back</button>}
            <button onClick={() => last ? onEnd(true) : setTour({ ...tour, i: tour.i + 1 })} data-testid="tour-next" className="gold-btn text-xs px-4 py-1.5 rounded-lg">{last ? "Finish" : "Next"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
